import React from 'react';
import { Autocomplete, Grid, TextField, Box, Dropdown, styled } from '@qbe/ads-core';
import { Controller, UseFormReturn } from 'react-hook-form';
import { get } from 'lodash';
import { usePrevious } from 'hooks/usePrevious';
import { AUSTRALIA, AUSTRALIAN_STATE } from 'utils/constants';
import { AutocompleteRenderInputParams } from '@mui/material';
import countries from 'resources/addresses';
import { useContent, useContentContext } from '@qbe/dfc-content';
import { CamelCase } from 'type-fest';
import { camelCase } from 'lodash';

type Prefix = '' | 'p' | 'r'; // physical or postal address or representative postal residential

type FieldCountry = CamelCase<`${Prefix} country`>;
type FieldState = CamelCase<`${Prefix} state`>;
type FieldPostcode = CamelCase<`${Prefix} postcode`>;
type FieldStreetAddress = CamelCase<`${Prefix} street address`>;
type FieldSuburb = CamelCase<`${Prefix} suburb`>;

type FieldName = FieldCountry | FieldState | FieldPostcode | FieldStreetAddress | FieldSuburb;

type AddressFields = Record<FieldName, any>;

type Field = {
  name?: string;
  label?: string;
  [key: `data-${string}`]: string;
};

type AddressManualProps<T extends AddressFields> = {
  prefix?: Prefix;
  form: UseFormReturn<T>;
  slotProps?: {
    country?: Field;
    streetAddress?: Field;
    state?: Field;
    suburb?: Field;
    postcode?: Field;
  };
};

const StyledAutocomplete = styled(Autocomplete)`
  .MuiAutocomplete-endAdornment > :nth-child(2) svg path {
    fill: #000000 !important;
  }
`;

export default function AddressManual<T extends AddressFields>({
  prefix = '',
  form,
  slotProps,
}: AddressManualProps<T>) {
  const {
    watch,
    setValue,
    formState: { errors },
    trigger,
    control,
    resetField,
  } = form as UseFormReturn<AddressFields>;

  const { getTextContent } = useContent({ context: useContentContext() });

  const fieldNameCountry = camelCase(`${prefix} country`) as FieldCountry;
  const fieldNameState = camelCase(`${prefix} state`) as FieldState;
  const fieldNamePostcode = camelCase(`${prefix} postcode`) as FieldPostcode;
  const fieldNameStreetAddress = camelCase(`${prefix} street address`) as FieldStreetAddress;
  const fieldNameSuburb = camelCase(`${prefix} suburb`) as FieldSuburb;

  const isCountryAU = watch(fieldNameCountry) === AUSTRALIA;
  const isComplaint = watch('lodgement' as any) === 'Complaint';

  const country = watch(fieldNameCountry);
  const state = watch(fieldNameState);
  const postcode = watch(fieldNamePostcode);

  // Cleanup - reset fields on unmount
  React.useEffect(() => {
    return () => {
      resetField(fieldNameState);
      resetField(fieldNamePostcode);
      resetField(fieldNameStreetAddress);
      resetField(fieldNameSuburb);
    };
  }, [fieldNameState, fieldNamePostcode, fieldNameStreetAddress, fieldNameSuburb, resetField]);

  React.useEffect(() => {
    if ((country || state) && postcode) {
      postcode !== ' ' && trigger(fieldNamePostcode);
    }
  }, [state, trigger, postcode, country, fieldNamePostcode]);

  const prevCountry = usePrevious(watch(fieldNameCountry));

  const handleChangeCountry = (fieldName: FieldName) => (e: React.SyntheticEvent, value: unknown) => {
    const selectedValue = (value ?? null) as string | null;
    setValue(fieldName, selectedValue);

    if (e) trigger(fieldName);

    if (prevCountry === AUSTRALIA || country === AUSTRALIA) {
      setValue(fieldNameState, '');
    } else if (state) {
      trigger(fieldNameState);
    }

    if (postcode) {
      trigger(fieldNamePostcode);
    }

    return selectedValue;
  };
  const handleInputChange = (e: React.SyntheticEvent, value: string): void => {
    if (!value && !isComplaint) setValue(fieldNameCountry, '');

    if (e) trigger(fieldNameCountry);
  };

  // eslint-disable-next-line react/display-name
  const CountryInput = (fieldName: string) => (params: AutocompleteRenderInputParams) =>
    (
      <TextField
        {...params}
        error={!!get(errors, fieldName)}
        helperText={get(errors, fieldName) ? get(errors, fieldName)?.message : null}
        name={fieldName}
        InputLabelProps={{
          ...params.InputLabelProps,
        }}
        label={getTextContent('address.country')}
        type="Combobox"
        data-track={fieldName}
        data-track-value="capture"
      />
    );

  return (
    <>
      <Box maxWidth={400}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <StyledAutocomplete
              multiple={false}
              shownIcon
              isOptionEqualToValue={(option, value) => option === value}
              value={watch(fieldNameCountry)}
              options={countries}
              onChange={handleChangeCountry(fieldNameCountry)}
              onInputChange={handleInputChange}
              renderInput={CountryInput(fieldNameCountry)}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={fieldNameStreetAddress}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value!}
                  error={!!error}
                  label={getTextContent('address.streetAddress')}
                  id={fieldNameStreetAddress}
                  data-track={fieldNameStreetAddress}
                  data-track-value="mask"
                  inputProps={{
                    maxLength: 225,
                  }}
                  helperText={error?.message}
                  {...slotProps?.streetAddress}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={fieldNameSuburb}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value!}
                  error={!!error}
                  label={getTextContent('address.suburb')}
                  id={fieldNameSuburb}
                  data-track={fieldNameSuburb}
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 225,
                  }}
                  helperText={error?.message}
                  {...slotProps?.suburb}
                />
              )}
            />
          </Grid>
          {isCountryAU && (
            <Grid item xs={12} sm={6} data-test="physical-state-wrapper">
              <Controller
                name={fieldNameState}
                control={control}
                render={({ field: { ref, ...field }, fieldState: { error } }) => {
                  return (
                    <Dropdown
                      {...field}
                      multiple={false}
                      error={!!error}
                      label={getTextContent('address.state')}
                      placeholder={getTextContent('address.state')}
                      id={field.name}
                      inputProps={
                        {
                          'data-track': field.name,
                          'data-track-value': 'capture',
                          'data-test': field.name,
                        } as any
                      }
                      optionList={AUSTRALIAN_STATE.map((state) => ({ label: state, value: state }))}
                      helperText={error?.message}
                      {...slotProps?.state}
                    />
                  );
                }}
              />
            </Grid>
          )}
          {!isCountryAU && (
            <Grid item xs={12} sm={6}>
              <Controller
                name={fieldNameState}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value!}
                    error={!!error}
                    label={getTextContent('address.state')}
                    id={fieldNameState}
                    data-track={fieldNameState}
                    data-track-value="capture"
                    maxLength={20}
                    helperText={error?.message}
                    {...slotProps?.state}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6} style={{ textAlign: 'right' }}>
            <Controller
              name={fieldNamePostcode}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value!}
                  error={!!error}
                  label={getTextContent('address.postcode')}
                  id={fieldNamePostcode}
                  data-track={fieldNamePostcode}
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 15,
                  }}
                  helperText={error?.message}
                  {...slotProps?.postcode}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
